import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram } from 'lucide-react';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', to: '/' },
    { name: 'About', to: '/about' },
    { name: 'Products', to: '/products' },
    { name: 'COA', to: '/products#coa' },
    { name: 'Contact', to: '/contact' }
  ];

  const productLinks = [
    { name: 'Liquid Bromine', to: '/products#bromine' },
    { name: 'Magnesium Chloride', to: '/products#magnesium-chloride' },
    { name: 'Certificate of Analysis', to: '/products#coa' },
    { name: 'Chemical Solutions', to: '/products#solutions' },
    { name: 'Industrial Chemicals', to: '/products#industrial' }
  ];

  const socialLinks = [
    { icon: <Facebook className="w-5 h-5" />, href: '#', label: 'Facebook' },
    { icon: <Instagram className="w-5 h-5" />, href: '#', label: 'Instagram' }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="lg:col-span-1">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center border border-gray-200 overflow-hidden">
                  <img 
                    src="/img/icons/tatvam_logo.svg" 
                    alt="Tatvam Chemicals" 
                    className="w-full h-full object-contain p-1"
                  />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Tatvam</h3>
                  <p className="text-sm text-gray-400">Chemicals</p>
                </div>
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Leading manufacturer and supplier of high-quality chemical products including 
                Liquid Bromine and Magnesium Chloride. Trusted by industries nationwide.
              </p>
              <div className="flex space-x-4">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-chemical-400 transition-colors duration-200"
                    aria-label={social.label}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                {quickLinks.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.to}
                      className="text-gray-300 hover:text-chemical-400 transition-colors duration-200"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Products */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Products</h4>
              <ul className="space-y-2">
                {productLinks.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.to}
                      className="text-gray-300 hover:text-chemical-400 transition-colors duration-200"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact Info</h4>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-chemical-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-300 text-sm">
                      TATVAM CHEMICALS LLP, Halvad Road, Nava Ghanshyam Gadh, Halvad, Dist. Morbi, Gujarat (India) - 363351
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-chemical-400 flex-shrink-0" />
                  <div>
                    <a 
                      href="tel:+919979953700" 
                      className="block text-gray-300 hover:text-chemical-400 transition-colors duration-200"
                    >
                      +91 99799 53700 - Parthbhai
                    </a>
                    <a 
                      href="tel:+919825118564" 
                      className="block text-gray-300 hover:text-chemical-400 transition-colors duration-200"
                    >
                      +91 98251 18564 - Akshaybhai
                    </a>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-chemical-400" />
                  <a 
                    href="mailto:tatvamchemicals@gmail.com"
                    className="text-gray-300 hover:text-chemical-400 transition-colors duration-200"
                  >
                    tatvamchemicals@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="border-t border-gray-800 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © {currentYear} Tatvam Chemicals LLP. All rights reserved.
            </p>
            <div className="mt-4 md:mt-0">
              <p className="text-gray-400 text-sm">
                GST Number: 24AARFT6743L1ZH
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 